// src/pages/Community/components/ReactionBar.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  onSnapshot,
  setDoc,
  serverTimestamp,
  collection,
  deleteDoc,
  getDoc,
  updateDoc,
  doc as fdDoc,
  increment,
} from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { docAt, withBase } from "./fbPath";
import { toast } from "react-toastify";
import "./css/ReactionBar.css";
import {
  enqueueReactionGive,
  enqueueReactionUndo,
} from "../../../services/xpService";

const DEBUG_REACTIONS = true;
const rxLog = (label, data = {}) => {
  if (!DEBUG_REACTIONS) return;
  try {
    const time = new Date().toISOString();
    console.groupCollapsed(
      `%c[Reaction] ${label} @ ${time}`,
      "color:#6a5acd;font-weight:600"
    );
    if (data && typeof data === "object") {
      const entries = Object.entries(data);
      if (entries.length) {
        const isFlat = entries.every(([, v]) => v === null || typeof v !== "object");
        isFlat ? console.table(data) : console.log(data);
      }
    }
    console.groupEnd();
  } catch {}
};
const rxError = (context, err, extra = {}) => {
  try {
    console.group(`%c[Reaction][ERROR] ${context}`, "color:#ff3b30;font-weight:700");
    console.error(err);
    if (extra && Object.keys(extra).length) console.log("extra:", extra);
    const code = err?.code || err?.error?.code;
    const message = err?.message || err?.error?.message;
    if (code || message) console.warn("FirebaseError:", { code, message });
    console.groupEnd();
  } catch {}
};

// use 'id' everywhere (matches existing code that expects r.id)
const REACTIONS = [
  { id: "like",       emoji: "👍", label: "Like" },
  { id: "love",       emoji: "❤️", label: "Love" },
  { id: "fire",       emoji: "🔥", label: "Hot" },
  { id: "applause",   emoji: "👏", label: "Applause" },
  { id: "celebrate",  emoji: "🎉", label: "Celebrate" },
  { id: "funny",      emoji: "😂", label: "Funny" },
  { id: "respect",    emoji: "🤝", label: "Respect" },
  { id: "100",        emoji: "💯", label: "100" },
  { id: "helpful",    emoji: "💡", label: "Helpful" },
  { id: "strategy",   emoji: "🎯", label: "Strategy" },
  { id: "epic",       emoji: "😎", label: "Epic Moment" },
  { id: "insightful", emoji: "🧠", label: "Insightful" },
  { id: "curious",    emoji: "❓", label: "Curious" },
  { id: "agree",      emoji: "✅", label: "Agree" },
  { id: "disagree",   emoji: "❌", label: "Disagree" },
  { id: "support",    emoji: "🤗", label: "Support" },
  { id: "bookmark",   emoji: "🔖", label: "Save" },
  { id: "bug",        emoji: "🐞", label: "Issue" },
  { id: "cool",       emoji: "🆒", label: "Cool" },
  { id: "wow",        emoji: "😮", label: "Wow" },
  { id: "music",      emoji: "🎶", label: "Music" },
];

export default function ReactionBar({
  basePath,
  sectionKey = "global",
  visibleReactions = null, // array of ids to show; null = show all
  highlight = null, // id to visually highlight
  onSelect = null, // callback: (reactionId|null) => void
}) {
  const auth = useMemo(() => getAuth(), []);
  const [user, setUser] = useState(() => auth.currentUser);
  const [mine, setMine] = useState(null); // my current reaction id
  const [counts, setCounts] = useState({}); // { helpful: 3, strategy: 1, ... }

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => setUser(u || null));
    return () => unsub();
  }, [auth]);

  const myDocRef = useMemo(
    () =>
      user && basePath?.length
        ? docAt(...withBase(basePath, "reactions", `${sectionKey}_${user.uid}`))
        : null,
    [user?.uid, sectionKey, JSON.stringify(basePath)]
  );

  async function getPostOwnerUid() {
    try {
      if (!basePath?.length) return null;
      const parentRef = docAt(...withBase(basePath));
      const snap = await getDoc(parentRef);
      const p = snap.data() || {};
      const owner =
        p.ownerUid || p.authorUid || p.uid || p.userId || p.createdBy || null;
      rxLog("owner resolved", { path: parentRef.path, owner });
      return owner;
    } catch (e) {
      rxError("getPostOwnerUid failed", e, { basePath });
      return null;
    }
  }

  // call server-side callable to update achievements (safer than client writes)
async function updateAchievementCounts(postId, deltaMap = {}, postOwnerUid = null) {
  if (!postId && !postOwnerUid) return;
  try {
    const functions = getFunctions();
    const fn = httpsCallable(functions, "updateAchievements");
    const payload = { postId: postId || "", deltaMap, source: "reaction", postOwnerUid };
    const res = await fn(payload);
    rxLog("updateAchievements callable result", { postId, postOwnerUid, res: res?.data });
    return res?.data ?? null;
  } catch (err) {
    rxError("updateAchievementCounts (callable) failed", err, { postId, deltaMap, postOwnerUid });
    return null;
  }
}


  // my reaction doc listener
  useEffect(() => {
    if (!myDocRef) {
      setMine(null);
      return;
    }
    return onSnapshot(
      myDocRef,
      (snap) => {
        const t = snap.exists() ? snap.data()?.type || null : null;
        rxLog("my reaction snapshot", { doc: myDocRef.path, type: t });
        setMine(t);
      },
      (err) => rxError("onSnapshot(myDocRef)", err, { doc: myDocRef.path })
    );
  }, [myDocRef]);

  // tally counts under basePath
  useEffect(() => {
    if (!basePath?.length) return;

    const parentDocRef = docAt(...withBase(basePath));
    const reactionsColRef = collection(parentDocRef, "reactions");
    const validIds = new Set(REACTIONS.map((r) => r.id));

    return onSnapshot(
      reactionsColRef,
      (snap) => {
        const next = Object.fromEntries(REACTIONS.map((r) => [r.id, 0]));

        snap.forEach((d) => {
          if (!d.id.startsWith(`${sectionKey}_`)) return;
          const t = d.data()?.type;
          if (validIds.has(t)) next[t] += 1;
        });

        rxLog("tally snapshot", { parent: parentDocRef.path, counts: next });
        setCounts(next);
      },
      (err) => rxError("onSnapshot(reactionsColRef)", err, { col: reactionsColRef.path })
    );
  }, [JSON.stringify(basePath), sectionKey]);

async function toggleReaction(typeId) {
  if (!user) {
    toast.info("Please sign in to react");
    return;
  }
  if (!myDocRef) {
    rxError("toggleReaction aborted (no myDocRef)", null, { basePath });
    return;
  }

  const postCollection = basePath?.[0] || "missions";
  const postId = basePath?.[1] || null;
  if (!postId) {
    rxError("toggleReaction aborted (no postId)", null, { basePath });
    return;
  }

  const isBug = typeId === "bug";
  const wasBug = mine === "bug";
  const willBe = mine === typeId ? null : typeId; // the intended final state

  rxLog("toggle start", {
    typeId,
    mine,
    willBe,
    postCollection,
    postId,
    isBug,
    wasBug,
    doc: myDocRef.path,
  });

  try {
    // Resolve owner UID up-front (callable requires it)
    const postOwnerUid = await getPostOwnerUid();
    if (!postOwnerUid) {
      // If you absolutely require an owner for achievements, log and continue — callable will reject.
      // You could also choose to `return` here if missing owner should stop the whole flow.
      rxLog("toggleReaction: postOwnerUid not found, continuing without owner (callable will fail if it requires owner)", { postId });
    }

    if (mine === typeId) {
      // === Unselect (user clicked the same reaction again) ===
      await deleteDoc(myDocRef);
      rxLog("doc deleted (unselect)", { doc: myDocRef.path, typeId });

      // decrement achievement for the owner (pass owner UID)
      try {
        await updateAchievementCounts(postId, { [typeId]: -1 }, postOwnerUid);
      } catch (e) {
        rxError("updateAchievementCounts failed (unselect)", e, { postId, typeId, postOwnerUid });
      }

      // enqueue XP undo for non-bug reactions
      if (!isBug && user?.uid) {
        await enqueueReactionUndo({
          giverUid: user.uid,
          postId,
          postOwnerUid,
          postCollection,
          reactionId: typeId,
        }).catch((e) =>
          rxError("enqueueReactionUndo failed", e, {
            postId,
            reactionId: typeId,
            postOwnerUid,
            postCollection,
          })
        );
      } else {
        rxLog("skip XP (bug unselect)", { reactionId: typeId });
      }
    } else {
      // === Selecting a new reaction (or switching from a different one) ===

      // If the user had a previous non-bug reaction, undo it first (XP + achievements)
      if (mine && !wasBug && user?.uid) {
        try {
          await enqueueReactionUndo({
            giverUid: user.uid,
            postId,
            postOwnerUid,
            postCollection,
            reactionId: mine,
          });
        } catch (e) {
          rxError("enqueueReactionUndo (switch) failed", e, {
            postId,
            oldReactionId: mine,
            postOwnerUid,
            postCollection,
          });
        }

        try {
          await updateAchievementCounts(postId, { [mine]: -1 }, postOwnerUid);
        } catch (e) {
          rxError("updateAchievementCounts failed (switch - decrement old)", e, {
            postId,
            oldReactionId: mine,
            postOwnerUid,
          });
        }
      }

      // Write new reaction (or switch)
      await setDoc(
        myDocRef,
        { type: typeId, createdAt: serverTimestamp() },
        { merge: true }
      );
      rxLog("doc set/merged (select)", { doc: myDocRef.path, typeId });

      // Increment achievements for the owner
      try {
        await updateAchievementCounts(postId, { [typeId]: 1 }, postOwnerUid);
      } catch (e) {
        rxError("updateAchievementCounts failed (select)", e, { postId, typeId, postOwnerUid });
      }

      // enqueue XP give for non-bug reactions
      if (!isBug && user?.uid) {
        await enqueueReactionGive({
          giverUid: user.uid,
          postId,
          postOwnerUid,
          postCollection,
          reactionId: typeId,
        }).catch((e) =>
          rxError("enqueueReactionGive failed", e, {
            postId,
            reactionId: typeId,
            postOwnerUid,
            postCollection,
          })
        );
      } else {
        rxLog("skip XP (bug select)", { reactionId: typeId });
      }
    }

    // Notify parent about the intended final state (best-effort)
    try {
      if (typeof onSelect === "function") onSelect(willBe);
    } catch (e) {
      rxError("onSelect callback failed", e);
    }

    rxLog("toggle end", { typeId, finalMine: willBe });
  } catch (e) {
    // Write / network failure fallback
    rxError("toggleReaction write failed", e, {
      postId,
      typeId,
      doc: myDocRef?.path,
    });
    toast.error("Failed to save reaction. Please try again.");
  }
}


  return (
    <div
      className={`reaction-bar ${mine ? "has-reaction" : ""}`}
      role="radiogroup"
      aria-label="Reactions"
    >
      {REACTIONS.filter(r => !visibleReactions || visibleReactions.includes(r.id)).map((r) => {
        const selected = mine === r.id;
        const count = counts[r.id] || 0;
        const isHighlighted = highlight === r.id;
        return (
          <button
            key={r.id}
            type="button"
            className={`msn-chip ${selected ? "is-selected" : ""} ${isHighlighted ? "is-highlighted" : ""}`}
            onClick={() => toggleReaction(r.id)}
            role="radio"
            aria-checked={selected}
            title={r.label}
          >
            <span aria-hidden>{r.emoji}</span>
            <span className="label">{r.label}</span>
            {count > 0 && (
              <span
                className={`reaction-count ${selected ? "is-my-reaction" : ""}`}
              >
                {count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
